import * as React from 'react';
import {
    Children,
    cloneElement,
    isValidElement,
    MouseEvent,
    ReactElement,
    useState,
} from 'react';
import { DataNode } from 'rc-tree/lib/interface';
import { Menu, styled } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { DeleteMenuItem } from './DeleteMenuItem';

/**
 * Provides a dropdown menu for each node.
 * It accepts children which must return a MaterialUI MenuItem component
 *
 * @example
 * import { NodeActions, DeleteMenuItem, TreeWithDetails } from '@react-admin/ra-tree';
 *
 * const MyCustomActionMenuItem = ({ record, resource, parentId }) => {
 *     const handleClick = () => {
 *         // Do something with dataProvider ?
 *     }
 *     return (
 *         <MenuItem onClick={handleClick}>
 *             Do something
 *         </MenuItem>
 *     );
 * };
 *
 * const MyActions = (props) => (
 *     <NodeActions {...props}>
 *         <MyCustomActionMenuItem />
 *         <DeleteMenuItem />
 *     </NodeActions>
 * );
 *
 * const CategoriesList: FC<Props> = props => (
 *     <TreeWithDetails
 *         titleField="name"
 *         edit={CategoriesEdit}
 *         draggable
 *         showLine
 *         nodeActions={<MyActions />}
 *         {...props}
 *     />
 * );
 */
const NodeActions = (props: NodeActionsProps): ReactElement => {
    const { children = <DeleteMenuItem />, className } = props;
    const [anchorEl, setAnchorEl] = useState(null);

    const handleClick = (event: MouseEvent): void => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (event: MouseEvent): void => {
        event.stopPropagation();
        setAnchorEl(null);
    };

    return (
        <Root className={className}>
            <button
                className={RaNodeActionsClasses.menuButton}
                onClick={handleClick}
            >
                <MoreVertIcon />
            </button>
            <Menu anchorEl={anchorEl} open={!!anchorEl} onClose={handleClose}>
                {Children.map(children, child =>
                    isValidElement<{ onClose: () => void }>(child)
                        ? cloneElement<{
                              onClose: (event: MouseEvent) => void;
                          }>(child, {
                              onClose: handleClose,
                          })
                        : child
                )}
            </Menu>
        </Root>
    );
};

export default NodeActions;

export interface NodeActionsProps {
    children?: ReactElement<{ onClose: (event: MouseEvent) => void }>|ReactElement<{ onClose: (event: MouseEvent) => void }>[];
    className?: string;
    data?: DataNode;
}

const PREFIX = 'RaNodeActions';
export const RaNodeActionsClasses = {
    menuButton: `${PREFIX}-menuButton`,
};
const Root = styled('div', {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})(() => ({}));
